import React, { useState, useEffect, useCallback } from "react";
import { useUser } from "@clerk/clerk-react";
import {
  Play,
  Pause,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  AlertCircle,
  Calendar,
  TrendingUp,
  RefreshCw,
  Eye,
  BarChart3,
  Zap,
  ChevronRight,
  ChevronDown,
  X,
} from "lucide-react";
import { apiUrl } from "../config/api";
import { authFetch } from "../utils/api";
import "./AutomationDashboard.css";

// Simple markdown renderer for AI summaries
const renderMarkdown = (text) => {
  if (!text) return null;

  // Split into lines and process
  const lines = text.split("\n");
  const elements = [];
  let inCodeBlock = false;
  let codeContent = [];
  let listItems = [];
  let listType = null;

  const flushList = () => {
    if (listItems.length > 0) {
      const ListTag = listType === "ol" ? "ol" : "ul";
      elements.push(
        <ListTag key={`list-${elements.length}`} className="md-list">
          {listItems.map((item, i) => (
            <li key={i}>{processInline(item)}</li>
          ))}
        </ListTag>,
      );
      listItems = [];
      listType = null;
    }
  };

  const processInline = (text) => {
    // Process inline markdown: **bold**, *italic*, `code`, [link](url)
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining) {
      // Bold: **text**
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      // Italic: *text*
      const italicMatch = remaining.match(/(?<!\*)\*([^*]+)\*(?!\*)/);
      // Inline code: `code`
      const codeMatch = remaining.match(/`([^`]+)`/);
      // Link: [text](url)
      const linkMatch = remaining.match(/\[([^\]]+)\]\(([^)]+)\)/);

      // Find earliest match
      const matches = [
        boldMatch && { type: "bold", match: boldMatch, index: boldMatch.index },
        italicMatch && {
          type: "italic",
          match: italicMatch,
          index: italicMatch.index,
        },
        codeMatch && { type: "code", match: codeMatch, index: codeMatch.index },
        linkMatch && { type: "link", match: linkMatch, index: linkMatch.index },
      ]
        .filter(Boolean)
        .sort((a, b) => a.index - b.index);

      if (matches.length === 0) {
        parts.push(remaining);
        break;
      }

      const earliest = matches[0];
      if (earliest.index > 0) {
        parts.push(remaining.slice(0, earliest.index));
      }

      switch (earliest.type) {
        case "bold":
          parts.push(<strong key={key++}>{earliest.match[1]}</strong>);
          remaining = remaining.slice(
            earliest.index + earliest.match[0].length,
          );
          break;
        case "italic":
          parts.push(<em key={key++}>{earliest.match[1]}</em>);
          remaining = remaining.slice(
            earliest.index + earliest.match[0].length,
          );
          break;
        case "code":
          parts.push(
            <code key={key++} className="md-inline-code">
              {earliest.match[1]}
            </code>,
          );
          remaining = remaining.slice(
            earliest.index + earliest.match[0].length,
          );
          break;
        case "link":
          parts.push(
            <a
              key={key++}
              href={earliest.match[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="md-link"
            >
              {earliest.match[1]}
            </a>,
          );
          remaining = remaining.slice(
            earliest.index + earliest.match[0].length,
          );
          break;
        default:
          remaining = "";
      }
    }

    return parts.length === 1 ? parts[0] : parts;
  };

  lines.forEach((line, idx) => {
    // Code block handling
    if (line.startsWith("```")) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${elements.length}`} className="md-code-block">
            <code>{codeContent.join("\n")}</code>
          </pre>,
        );
        codeContent = [];
        inCodeBlock = false;
      } else {
        flushList();
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeContent.push(line);
      return;
    }

    // Headers
    const h3Match = line.match(/^### (.+)/);
    const h2Match = line.match(/^## (.+)/);
    const h1Match = line.match(/^# (.+)/);

    if (h3Match) {
      flushList();
      elements.push(
        <h4 key={`h3-${idx}`} className="md-h3">
          {processInline(h3Match[1])}
        </h4>,
      );
      return;
    }
    if (h2Match) {
      flushList();
      elements.push(
        <h3 key={`h2-${idx}`} className="md-h2">
          {processInline(h2Match[1])}
        </h3>,
      );
      return;
    }
    if (h1Match) {
      flushList();
      elements.push(
        <h2 key={`h1-${idx}`} className="md-h1">
          {processInline(h1Match[1])}
        </h2>,
      );
      return;
    }

    // Bullet lists
    const bulletMatch = line.match(/^[\s]*[-*•]\s+(.+)/);
    if (bulletMatch) {
      if (listType !== "ul") flushList();
      listType = "ul";
      listItems.push(bulletMatch[1]);
      return;
    }

    // Numbered lists
    const numberedMatch = line.match(/^[\s]*\d+\.\s+(.+)/);
    if (numberedMatch) {
      if (listType !== "ol") flushList();
      listType = "ol";
      listItems.push(numberedMatch[1]);
      return;
    }

    // Empty line
    if (!line.trim()) {
      flushList();
      return;
    }

    // Regular paragraph
    flushList();
    elements.push(
      <p key={`p-${idx}`} className="md-paragraph">
        {processInline(line)}
      </p>,
    );
  });

  flushList();

  return <div className="md-content">{elements}</div>;
};

const AutomationDashboard = ({ onClose }) => {
  const { user } = useUser();
  const userId =
    user?.id ||
    process.env.REACT_APP_DEV_USER_ID ||
    "user_35aUqUMMlEPmKiobX3T3rDS352U";
  const [automations, setAutomations] = useState([]);
  const [selectedAutomation, setSelectedAutomation] = useState(null);
  const [runHistory, setRunHistory] = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRuns, setExpandedRuns] = useState(new Set());
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [activeResultsTab, setActiveResultsTab] = useState("summary"); // 'summary' | 'tools' | 'json'
  // hf facelift: className-only state for the one-shot resume glow beat
  const [justResumedId, setJustResumedId] = useState(null);

  // Timezone handling - use user's local timezone instead of hardcoded GMT
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Format date with user's timezone - enhanced for consistency
  const formatDate = (dateString) => {
    if (!dateString) return "Never";

    // Handle both ISO strings and timestamps
    let date;
    if (typeof dateString === "number") {
      date = new Date(dateString);
    } else {
      date = new Date(dateString);
    }

    // Validate date
    if (isNaN(date.getTime())) return "Invalid Date";

    // Format with explicit timezone to prevent GMT display
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: userTimezone,
      timeZoneName: "short",
    }).format(date);
  };

  // Format cron schedule to human-readable
  const formatSchedule = (cronString) => {
    if (!cronString) return "No schedule";

    // Parse cron: minute hour day month dayOfWeek
    const parts = cronString.split(" ");
    if (parts.length !== 5) return cronString;

    const [minute, hour, , , dayOfWeek] = parts;

    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const dayName = days[parseInt(dayOfWeek)] || "Unknown";

    // Convert 24h to 12h format
    const hourNum = parseInt(hour);
    const minuteNum = parseInt(minute);
    const period = hourNum >= 12 ? "PM" : "AM";
    const hour12 = hourNum % 12 || 12;
    const minuteStr = minuteNum.toString().padStart(2, "0");

    return `Every ${dayName} at ${hour12}:${minuteStr} ${period}`;
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return "N/A";
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Fetch automations
  const fetchAutomations = useCallback(async () => {
    try {
      const response = await authFetch(apiUrl(`/api/automations`));
      if (!response.ok) throw new Error("Failed to fetch automations");
      const data = await response.json();
      setAutomations(data.automations);
    } catch (err) {
      console.error("Failed to fetch automations:", err);
      setError(err.message);
    }
  }, [userId]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await authFetch(
        apiUrl(`/api/automations/stats/overview`),
      );
      if (!response.ok) throw new Error("Failed to fetch stats");
      const data = await response.json();
      setStats(data);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, [userId]);

  // Fetch run history for selected automation
  const fetchRunHistory = useCallback(async (automationId) => {
    try {
      const response = await authFetch(
        apiUrl(`/api/automations/${automationId}/runs?limit=50`),
      );
      if (!response.ok) throw new Error("Failed to fetch run history");
      const data = await response.json();
      setRunHistory(data.runs);
    } catch (err) {
      console.error("Failed to fetch run history:", err);
    }
  }, []);

  // Fetch detailed run information
  const fetchRunDetails = useCallback(async (automationId, runId) => {
    try {
      const response = await authFetch(
        apiUrl(`/api/automations/${automationId}/runs/${runId}`),
      );
      if (!response.ok) throw new Error("Failed to fetch run details");
      const data = await response.json();
      setSelectedRun(data);
    } catch (err) {
      console.error("Failed to fetch run details:", err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchAutomations(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchAutomations, fetchStats]);

  // Auto-refresh
  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchAutomations();
      fetchStats();
      if (selectedAutomation) {
        fetchRunHistory(selectedAutomation.id);
      }
    }, 10000); // Refresh every 10 seconds
    return () => clearInterval(interval);
  }, [
    autoRefresh,
    selectedAutomation,
    fetchAutomations,
    fetchStats,
    fetchRunHistory,
  ]);

  // Select automation
  const handleSelectAutomation = async (automation) => {
    setSelectedAutomation(automation);
    setSelectedRun(null);
    await fetchRunHistory(automation.id);
  };

  // Trigger automation run
  const handleRunAutomation = async (automationId) => {
    try {
      const response = await authFetch(
        apiUrl(`/api/automations/${automationId}/run`),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        },
      );
      if (!response.ok) throw new Error("Failed to trigger automation");

      // Refresh after short delay
      setTimeout(() => {
        fetchAutomations();
        if (selectedAutomation?.id === automationId) {
          fetchRunHistory(automationId);
        }
      }, 1000);
    } catch (err) {
      console.error("Failed to run automation:", err);
      alert(`Failed to run automation: ${err.message}`);
    }
  };

  // Pause automation. Webhook (Harry trigger) rows live in a different
  // backend table — routing them to the automation endpoints 404'd, so
  // pause/delete on event automations never worked from this dashboard.
  const handlePauseAutomation = async (automationId, triggerType) => {
    try {
      const response =
        triggerType === "webhook"
          ? await authFetch(apiUrl(`/api/harry/triggers/${automationId}/toggle`), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ active: false }),
            })
          : await authFetch(apiUrl(`/api/automations/${automationId}/pause`), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
            });
      if (!response.ok) throw new Error("Failed to pause automation");
      setJustResumedId(null); // className toggle only — allows the glow to replay on next resume
      await fetchAutomations();
    } catch (err) {
      console.error("Failed to pause automation:", err);
      alert(`Failed to pause automation: ${err.message}`);
    }
  };

  // Resume automation
  const handleResumeAutomation = async (automationId, triggerType) => {
    try {
      const response =
        triggerType === "webhook"
          ? await authFetch(apiUrl(`/api/harry/triggers/${automationId}/toggle`), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ active: true }),
            })
          : await authFetch(apiUrl(`/api/automations/${automationId}/resume`), {
              method: "POST",
              headers: { "Content-Type": "application/json" },
            });
      if (!response.ok) throw new Error("Failed to resume automation");
      setJustResumedId(automationId); // className toggle only — one-shot hf-glow-success beat
      await fetchAutomations();
    } catch (err) {
      console.error("Failed to resume automation:", err);
      alert(`Failed to resume automation: ${err.message}`);
    }
  };

  // Delete automation
  const handleDeleteAutomation = async (automationId, triggerType) => {
    if (!window.confirm("Are you sure you want to delete this automation?"))
      return;

    try {
      const response = await authFetch(
        apiUrl(
          triggerType === "webhook"
            ? `/api/harry/triggers/${automationId}`
            : `/api/automations/${automationId}`,
        ),
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        },
      );
      if (!response.ok) throw new Error("Failed to delete automation");

      // Clear selection if deleted automation was selected
      if (selectedAutomation?.id === automationId) {
        setSelectedAutomation(null);
        setRunHistory([]);
      }

      await fetchAutomations();
      await fetchStats();
    } catch (err) {
      console.error("Failed to delete automation:", err);
      alert(`Failed to delete automation: ${err.message}`);
    }
  };

  // Toggle run expansion
  const toggleRunExpansion = (runId) => {
    const newExpanded = new Set(expandedRuns);
    if (newExpanded.has(runId)) {
      newExpanded.delete(runId);
    } else {
      newExpanded.add(runId);
    }
    setExpandedRuns(newExpanded);
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: "Active", className: "status-active", icon: Activity },
      paused: { label: "Paused", className: "status-paused", icon: Pause },
      running: {
        label: "Running",
        className: "status-running",
        icon: RefreshCw,
      },
      completed: {
        label: "Completed",
        className: "status-completed",
        icon: CheckCircle,
      },
      failed: { label: "Failed", className: "status-failed", icon: XCircle },
      error: { label: "Error", className: "status-error", icon: AlertCircle },
    };

    const config = statusConfig[status] || statusConfig.active;
    const Icon = config.icon;

    return (
      <span
        className={`status-badge ${config.className}${
          status === "active" || status === "running" ? " hf-breathe" : ""
        }`}
      >
        <Icon size={14} />
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="automation-dashboard-overlay" onClick={onClose}>
        <div
          className="automation-dashboard"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="loading-container">
            <RefreshCw size={48} className="spin" />
            <p>Loading automations...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="automation-dashboard-overlay" onClick={onClose}>
        <div
          className="automation-dashboard"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="error-container">
            <AlertCircle size={48} />
            <h2>Error Loading Automations</h2>
            <p>{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="automation-dashboard-overlay" onClick={onClose}>
      <div
        className="automation-dashboard"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <header className="dashboard-header">
          <div className="header-content">
            <div className="header-left">
              <img
                src="/Optimus Logos and Graphics/Optimus-Logo-Header.svg"
                alt="Optimus OS"
                className="header-logo"
                style={{ height: "32px", marginRight: "12px" }}
              />
              <h1>
                <Zap size={32} />
                Automation Dashboard
              </h1>
            </div>
            <div className="header-actions">
              <span className="timezone-indicator">
                <Clock size={16} />
                {userTimezone.split("/")[1] || userTimezone}
              </span>
              <button
                className={`btn-icon ${autoRefresh ? "active" : ""}`}
                onClick={() => setAutoRefresh(!autoRefresh)}
                title={
                  autoRefresh ? "Auto-refresh enabled" : "Auto-refresh disabled"
                }
              >
                <RefreshCw
                  size={20}
                  className={autoRefresh ? "spin-slow" : ""}
                />
              </button>
              <button className="btn-icon" onClick={onClose} title="Close">
                <X size={20} />
              </button>
            </div>
          </div>
        </header>

        {/* Stats Overview */}
        {stats && (
          <div className="stats-overview hf-stagger">
            <div className="stat-card">
              <div className="stat-icon active">
                <Activity size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Active Automations</span>
                <span className="stat-value hf-count">{stats.automations.active}</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon runs">
                <BarChart3 size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Total Runs</span>
                <span className="stat-value hf-count">{stats.runs.total}</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon success">
                <CheckCircle size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Success Rate</span>
                <span className="stat-value hf-count">{stats.runs.successRate}%</span>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon trend">
                <TrendingUp size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Self-Healed</span>
                <span className="stat-value hf-count">
                  {stats.healing?.totalHealings || 0}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="dashboard-content">
          {/* Automations List */}
          <div className="automations-panel">
            <div className="panel-header">
              <h2>Your Automations ({automations.length})</h2>
            </div>

            <div className="automations-list hf-stagger">
              {automations.length === 0 ? (
                <div className="empty-state hf-reveal">
                  <Activity size={48} />
                  <h3>No Automations Yet</h3>
                  <p>
                    Start a conversation with Optimus to create your first
                    automation
                  </p>
                </div>
              ) : (
                automations.map((automation) => (
                  <div
                    key={automation.id}
                    className={`automation-card hf-lift ${selectedAutomation?.id === automation.id ? "selected" : ""}${justResumedId === automation.id ? " hf-glow-success" : ""}`}
                    onClick={() => handleSelectAutomation(automation)}
                  >
                    <div className="automation-header">
                      <h3>{automation.name}</h3>
                      {getStatusBadge(automation.status)}
                    </div>

                    {automation.description && (
                      <p className="automation-description">
                        {automation.description}
                      </p>
                    )}

                    <div className="automation-meta">
                      <div className="meta-item">
                        <Calendar size={14} />
                        <span>
                          {automation.triggerType === "webhook"
                            ? "⚡ Event webhook"
                            : formatSchedule(automation.schedule?.cron)}
                        </span>
                      </div>
                      <div className="meta-item">
                        <Clock size={14} />
                        <span>{formatDate(automation.lastRunAt)}</span>
                      </div>
                    </div>
                    {automation.triggerType === "webhook" && automation.hookUrl && (
                      <div className="meta-item" style={{ marginTop: 6 }}>
                        <code
                          className="select-all"
                          style={{ fontSize: 11, wordBreak: "break-all", opacity: 0.8 }}
                          title="Paste this into the app that should trigger it"
                        >
                          {automation.hookUrl}
                        </code>
                      </div>
                    )}

                    <div className="automation-stats">
                      <div className="stat-mini success">
                        <CheckCircle size={16} />
                        {automation.stats.successfulRuns}
                      </div>
                      <div className="stat-mini failed">
                        <XCircle size={16} />
                        {automation.stats.failedRuns}
                      </div>
                      <div className="stat-mini rate">
                        {automation.stats.successRate}% success
                      </div>
                    </div>

                    <div
                      className="automation-actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {automation.triggerType !== "webhook" && (
                        <button
                          className="btn-icon-small"
                          onClick={() => handleRunAutomation(automation.id)}
                          title="Run now"
                        >
                          <Play size={16} />
                        </button>
                      )}

                      {automation.status === "active" ? (
                        <button
                          className="btn-icon-small"
                          onClick={() =>
                            handlePauseAutomation(
                              automation.id,
                              automation.triggerType,
                            )
                          }
                          title="Pause"
                        >
                          <Pause size={16} />
                        </button>
                      ) : (
                        <button
                          className="btn-icon-small"
                          onClick={() =>
                            handleResumeAutomation(
                              automation.id,
                              automation.triggerType,
                            )
                          }
                          title="Resume"
                        >
                          <Play size={16} />
                        </button>
                      )}

                      <button
                        className="btn-icon-small danger"
                        onClick={() =>
                          handleDeleteAutomation(
                            automation.id,
                            automation.triggerType,
                          )
                        }
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Run History & Details */}
          <div className="details-panel">
            {!selectedAutomation ? (
              <div className="empty-state hf-reveal">
                <Eye size={48} />
                <h3>Select an Automation</h3>
                <p>
                  Choose an automation to view its execution history and details
                </p>
              </div>
            ) : (
              <>
                {/* Run History */}
                <div className="run-history">
                  <div className="panel-header">
                    <h2>Run History</h2>
                    <span className="run-count">{runHistory.length} runs</span>
                  </div>

                  <div className="runs-list">
                    {runHistory.length === 0 ? (
                      <div className="empty-state-small hf-reveal">
                        <p>No runs yet for this automation</p>
                      </div>
                    ) : (
                      runHistory.map((run) => (
                        <div key={run.id} className="run-item hf-reveal">
                          <div
                            className="run-header"
                            onClick={() => {
                              toggleRunExpansion(run.id);
                              if (!expandedRuns.has(run.id)) {
                                fetchRunDetails(selectedAutomation.id, run.id);
                              }
                            }}
                          >
                            <div className="run-header-left">
                              {expandedRuns.has(run.id) ? (
                                <ChevronDown size={20} />
                              ) : (
                                <ChevronRight size={20} />
                              )}
                              {getStatusBadge(run.status)}
                              <span className="run-date">
                                {formatDate(run.startedAt)}
                              </span>
                            </div>

                            <div className="run-header-right">
                              <span className="run-duration">
                                {formatDuration(run.duration)}
                              </span>
                              {run.stepsExecuted && (
                                <span className="run-steps">
                                  {run.stepsExecuted} steps
                                  {run.stepsFailed > 0 && (
                                    <span className="steps-failed">
                                      ({run.stepsFailed} failed)
                                    </span>
                                  )}
                                </span>
                              )}
                            </div>
                          </div>

                          {expandedRuns.has(run.id) && (
                            <div className="run-details">
                              {selectedRun?.id === run.id ? (
                                <>
                                  {/* Error Message */}
                                  {selectedRun.error && (
                                    <div className="error-box">
                                      <AlertCircle size={20} />
                                      <div>
                                        <strong>Error:</strong>
                                        <p>{selectedRun.error}</p>
                                      </div>
                                    </div>
                                  )}

                                  {/* Results */}
                                  {selectedRun.results &&
                                  Array.isArray(selectedRun.results) &&
                                  selectedRun.results.length > 0 ? (
                                    <div className="results-section">
                                      <div className="results-header">
                                        <h4>
                                          Results ({selectedRun.results.length}{" "}
                                          steps)
                                        </h4>
                                        <div className="results-tabs">
                                          <button
                                            className={`tab-btn ${activeResultsTab === "summary" ? "active" : ""}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveResultsTab("summary");
                                            }}
                                          >
                                            Summary
                                          </button>
                                          <button
                                            className={`tab-btn ${activeResultsTab === "tools" ? "active" : ""}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveResultsTab("tools");
                                            }}
                                          >
                                            Tool Calls
                                          </button>
                                          <button
                                            className={`tab-btn ${activeResultsTab === "json" ? "active" : ""}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setActiveResultsTab("json");
                                            }}
                                          >
                                            Raw JSON
                                          </button>
                                        </div>
                                      </div>

                                      {/* Summary Tab - AI Response */}
                                      {activeResultsTab === "summary" && (
                                        <div className="summary-content">
                                          {selectedRun.results.find(
                                            (r) => r.aiSummary,
                                          )?.aiSummary ? (
                                            <div className="ai-summary">
                                              <div className="ai-summary-text">
                                                {renderMarkdown(
                                                  selectedRun.results.find(
                                                    (r) => r.aiSummary,
                                                  ).aiSummary,
                                                )}
                                              </div>
                                            </div>
                                          ) : (
                                            <div className="no-summary">
                                              <p>
                                                No AI summary available for this
                                                run.
                                              </p>
                                              <p className="hint">
                                                Summary is generated for newer
                                                automations. Check the Tool
                                                Calls tab for execution details.
                                              </p>
                                            </div>
                                          )}
                                        </div>
                                      )}

                                      {/* Raw JSON Tab */}
                                      {activeResultsTab === "json" && (
                                        <pre className="code-block">
                                          {JSON.stringify(
                                            selectedRun.results,
                                            null,
                                            2,
                                          )}
                                        </pre>
                                      )}

                                      {/* Tool Calls Tab */}
                                      {activeResultsTab === "tools" && (
                                        <div className="results-summary">
                                          {selectedRun.results.map(
                                            (result, idx) => (
                                              <div
                                                key={idx}
                                                className="result-item"
                                              >
                                                <div className="result-step">
                                                  <span className="step-number">
                                                    Step {idx + 1}
                                                  </span>
                                                  <span
                                                    className={`step-status ${result.success ? "success" : "failed"}`}
                                                  >
                                                    {result.success ? (
                                                      <CheckCircle size={14} />
                                                    ) : (
                                                      <XCircle size={14} />
                                                    )}
                                                    <span className="step-tool">
                                                      {result.tool ||
                                                        result.stepId ||
                                                        "Unknown"}
                                                    </span>
                                                  </span>
                                                </div>
                                                {result.result?.content
                                                  ?.formatted_people &&
                                                  result.result.content
                                                    .formatted_people.length >
                                                    0 && (
                                                    <div className="people-found">
                                                      <strong>
                                                        Found{" "}
                                                        {
                                                          result.result.content
                                                            .formatted_people
                                                            .length
                                                        }{" "}
                                                        people:
                                                      </strong>
                                                      <div className="people-list">
                                                        {result.result.content.formatted_people
                                                          .slice(0, 3)
                                                          .map(
                                                            (person, pIdx) => (
                                                              <div
                                                                key={pIdx}
                                                                className="person-preview"
                                                              >
                                                                • {person.name}{" "}
                                                                - {person.title}{" "}
                                                                at{" "}
                                                                {person.company}
                                                              </div>
                                                            ),
                                                          )}
                                                        {result.result.content
                                                          .formatted_people
                                                          .length > 3 && (
                                                          <div className="person-preview">
                                                            +{" "}
                                                            {result.result
                                                              .content
                                                              .formatted_people
                                                              .length - 3}{" "}
                                                            more...
                                                          </div>
                                                        )}
                                                      </div>
                                                    </div>
                                                  )}
                                                {result.result?.content
                                                  ?.person && (
                                                  <div className="people-found">
                                                    <strong>Enriched:</strong>{" "}
                                                    {
                                                      result.result.content
                                                        .person.name
                                                    }{" "}
                                                    (
                                                    {
                                                      result.result.content
                                                        .person.email
                                                    }
                                                    )
                                                  </div>
                                                )}
                                                {result.success &&
                                                  !result.result?.content
                                                    ?.formatted_people &&
                                                  !result.result?.content
                                                    ?.person && (
                                                    <div className="step-success-message">
                                                      ✓ Completed successfully
                                                    </div>
                                                  )}
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="no-results">
                                      <p>
                                        No detailed results available for this
                                        run.
                                      </p>
                                    </div>
                                  )}

                                  {/* Progress Log */}
                                  {selectedRun.progressLog &&
                                    selectedRun.progressLog.length > 0 && (
                                      <div className="progress-log">
                                        <h4>Execution Log</h4>
                                        <div className="log-entries">
                                          {selectedRun.progressLog.map(
                                            (entry, idx) => (
                                              <div
                                                key={idx}
                                                className="log-entry"
                                              >
                                                <span className="log-timestamp">
                                                  {formatDate(entry.timestamp)}
                                                </span>
                                                <span
                                                  className={`log-type log-type-${entry.type}`}
                                                >
                                                  {entry.type}
                                                </span>
                                                <span className="log-message">
                                                  {entry.content}
                                                </span>
                                              </div>
                                            ),
                                          )}
                                        </div>
                                      </div>
                                    )}
                                </>
                              ) : (
                                <div className="loading-small">
                                  <RefreshCw size={16} className="spin" />
                                  Loading details...
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutomationDashboard;
